import {
  getTradePnl,
  getTradeTimestampMs,
  normalizeTradeStatus,
  resolveTradeGrossPnl,
  type AnyTrade,
} from './authoritativeTradeData';
import { buildDailyDataFromTrades } from './authoritativeTradeData';
import { filterCanonicalRealizedTrades } from './dashboardTradeSource';

/**
 * Trade-level statistics derived from INDIVIDUAL authoritative trade records.
 *
 * Widgets whose names refer to trades (winning/losing counts, gross P&L, profit
 * factor, max consecutive wins/losses, average win/loss) MUST derive from these
 * records — never from daily P&L buckets. Daily widgets keep using their daily
 * aggregates; this module exists only for the trade-labeled surfaces.
 */

export type WidgetTimeframeTokenLike = '24H' | '7D' | '30D' | '12M' | 'YTD' | undefined | null;

export type TradeLevelStats = {
  wins: number;
  losses: number;
  breakeven: number;
  grossWinningPnl: number;
  grossLosingPnl: number;
  netPnl: number;
  profitFactor: number;
  avgWinningTrade: number;
  avgLosingTrade: number;
  winRate: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
};

export function filterTradesByWidgetTimeframe<T extends AnyTrade>(trades: T[], timeframe: WidgetTimeframeTokenLike): T[] {
  if (!Array.isArray(trades) || trades.length === 0 || !timeframe) return trades;

  const now = Date.now();
  const tfDays = timeframe === '24H' ? 1 : timeframe === '7D' ? 7 : timeframe === '30D' ? 30 : timeframe === '12M' ? 365 : 0;
  const startOfYearMs = (() => {
    const n = new Date();
    return new Date(n.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
  })();

  return trades.filter((trade) => {
    const ts = getTradeTimestampMs(trade);
    if (ts == null) return false;
    if (timeframe === 'YTD') return ts >= startOfYearMs;
    return ts >= now - tfDays * 24 * 60 * 60 * 1000;
  });
}

export function computeTradeLevelStats(trades: AnyTrade[]): TradeLevelStats {
  const rows: { ts: number; pnl: number }[] = [];
  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  let netWinningPnl = 0;
  let netLosingPnl = 0;
  let trueGrossWinningPnl = 0;
  let trueGrossLosingPnl = 0;
  let allClosedTradesHaveGross = true;
  let hasClosedTrades = false;

  for (const raw of trades) {
    if (normalizeTradeStatus(raw) !== 'closed') continue;
    const pnl = getTradePnl(raw);
    if (!Number.isFinite(pnl)) continue;
    hasClosedTrades = true;
    // Consecutive outcomes are determined when trades are realized. Entry-time
    // ordering can misstate streaks when positions overlap and close out of order.
    rows.push({ ts: resolveCloseTimestampMs(raw) ?? getTradeTimestampMs(raw) ?? 0, pnl });

    // Net classification & sums (preserves win rate, net P&L, payoff ratio, and streaks)
    if (pnl > 0) {
      wins += 1;
      netWinningPnl += pnl;
    } else if (pnl < 0) {
      losses += 1;
      netLosingPnl += pnl;
    } else {
      breakeven += 1;
    }

    // Genuine Gross P&L resolution for Profit Factor calculation
    const gross = resolveTradeGrossPnl(raw);
    if (gross != null && Number.isFinite(gross)) {
      if (gross > 0) {
        trueGrossWinningPnl += gross;
      } else if (gross < 0) {
        trueGrossLosingPnl += gross;
      }
    } else {
      allClosedTradesHaveGross = false;
    }
  }

  // Consecutive streaks follow realized close order (falling back to entry
  // time only for legacy records without a close timestamp).
  rows.sort((a, b) => a.ts - b.ts);
  let maxConsecutiveWins = 0;
  let maxConsecutiveLosses = 0;
  let currentWins = 0;
  let currentLosses = 0;
  for (const row of rows) {
    if (row.pnl > 0) {
      currentWins += 1;
      currentLosses = 0;
      maxConsecutiveWins = Math.max(maxConsecutiveWins, currentWins);
    } else if (row.pnl < 0) {
      currentLosses += 1;
      currentWins = 0;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentLosses);
    } else {
      currentWins = 0;
      currentLosses = 0;
    }
  }

  // Profit factor standard definition:
  // Sum of gross profits / Absolute sum of gross losses
  // If gross P&L is unavailable or cannot be reliably determined for all trades, profitFactor cannot be reliably computed.
  // When gross loss is 0 (or no losses), profitFactor is 0 (or N/A at display level).
  const absGrossLosingPnl = Math.abs(trueGrossLosingPnl);
  let profitFactor = 0;
  if (hasClosedTrades && allClosedTradesHaveGross && absGrossLosingPnl > 0) {
    profitFactor = trueGrossWinningPnl / absGrossLosingPnl;
  } else if (!allClosedTradesHaveGross) {
    // If gross P&L cannot be reliably determined for some trades, we do NOT invent gross values.
    // We signal this via profitFactor = 0 (and UI checks if grossLoss > 0 / availability).
    profitFactor = 0;
  }

  return {
    wins,
    losses,
    breakeven,
    grossWinningPnl: allClosedTradesHaveGross ? trueGrossWinningPnl : netWinningPnl,
    grossLosingPnl: allClosedTradesHaveGross ? trueGrossLosingPnl : netLosingPnl,
    netPnl: netWinningPnl + netLosingPnl,
    profitFactor,
    // Win/loss counts classify trades by net realized P&L, so their averages
    // must use the same net amounts. Gross sums remain available separately
    // for Profit Factor.
    avgWinningTrade: wins > 0 ? netWinningPnl / wins : 0,
    avgLosingTrade: losses > 0 ? netLosingPnl / losses : 0,
    winRate: wins + losses > 0 ? wins / (wins + losses) : 0,
    maxConsecutiveWins,
    maxConsecutiveLosses,
  };
}

export type CanonicalPerformanceStats = TradeLevelStats & {
  totalPnl: number;
  avgTradePnl: number;
  largestGain: number;
  largestLoss: number;
  avgHoldMinutes: number;
  avgHoldMinutesWinning: number;
  avgHoldMinutesLosing: number;
};

function resolveCloseTimestampMs(trade: AnyTrade): number | null {
  const candidates = [
    trade?.closeTimestampMs,
    trade?.exitTimestampMs,
    trade?.exitTimestamp,
    trade?.exitTime,
    trade?.closeTime,
    trade?.closedAt,
  ];
  for (const candidate of candidates) {
    const ts = candidate == null ? null : Number(candidate);
    if (ts != null && Number.isFinite(ts) && ts > 0) return ts;
  }
  return null;
}

function averageHoldMinutes(list: AnyTrade[]): number {
  if (list.length === 0) return 0;
  let totalMinutes = 0;
  for (const trade of list) {
    const open = getTradeTimestampMs(trade);
    const close = resolveCloseTimestampMs(trade);
    if (open != null && close != null && close >= open) {
      totalMinutes += (close - open) / 60000;
    }
  }
  return totalMinutes / list.length;
}

/**
 * THE canonical statistics engine. Every page (Dashboard, Trades, Calendar,
 * Journal, Trend, WinVsLoss) and every performance widget MUST derive realized
 * statistics from this single function over the canonical filtered collection
 * (`useCanonicalStatisticsTrades`). It applies the same per-trade P&L resolver
 * (`getTradePnl`), the same open-trade exclusion, and the same win/loss split
 * everywhere — no surface may recompute realized P&L independently.
 */
export function computeCanonicalPerformanceStats(trades: AnyTrade[]): CanonicalPerformanceStats {
  const stats = computeTradeLevelStats(trades);

  const closed = Array.isArray(trades)
    ? trades.filter((trade) => normalizeTradeStatus(trade) === 'closed')
    : [];
  const pnls = closed
    .map((trade) => getTradePnl(trade))
    .filter((pnl) => Number.isFinite(pnl));
  const totalPnl = pnls.reduce((sum, pnl) => sum + pnl, 0);
  const gains = pnls.filter((pnl) => pnl > 0);
  const losses = pnls.filter((pnl) => pnl < 0);

  const winningTrades = closed.filter((trade) => getTradePnl(trade) > 0);
  const losingTrades = closed.filter((trade) => getTradePnl(trade) < 0);

  return {
    ...stats,
    totalPnl,
    avgTradePnl: pnls.length > 0 ? totalPnl / pnls.length : 0,
    largestGain: gains.length > 0 ? Math.max(...gains) : 0,
    largestLoss: losses.length > 0 ? Math.min(...losses) : 0,
    avgHoldMinutes: averageHoldMinutes(closed),
    avgHoldMinutesWinning: averageHoldMinutes(winningTrades),
    avgHoldMinutesLosing: averageHoldMinutes(losingTrades),
  };
}

/** Builds the canonical daily series from the canonical filtered collection. */
export function buildCanonicalDailyData(trades: AnyTrade[], days: number, endDate?: Date) {
  return buildDailyDataFromTrades(filterCanonicalRealizedTrades(trades), days, endDate);
}
